import { get, writable } from 'svelte/store';
import { api } from './api.js';
import { auth } from './auth.js';
import type { ApiResponse } from './apiTypes.js';
import {
  getCachedTeamUsage,
  setCachedTeamUsage,
  shouldUpdateTeamUsageDisplay,
  type TeamUsageSnapshot,
} from './teamBillingCache.js';

export interface TeamBillingState {
  teamUuid: string | null;
  usage: TeamUsageSnapshot | null;
  /** True only when we have a team but no display data yet (first load). */
  loading: boolean;
}

function initialState(): TeamBillingState {
  const teamUuid = get(auth)?.user?.active_team_uuid ?? null;
  if (!teamUuid) {
    return { teamUuid: null, usage: null, loading: false };
  }
  const cached = getCachedTeamUsage(teamUuid);
  return { teamUuid, usage: cached, loading: !cached };
}

const store = writable<TeamBillingState>(initialState());

let trackedTeamUuid: string | null = null;
let inFlightFor: string | null = null;

function applyUsage(uuid: string, next: TeamUsageSnapshot) {
  store.update((state) => {
    if (state.teamUuid !== uuid) return state;
    if (!shouldUpdateTeamUsageDisplay(state.usage, next)) {
      return { ...state, loading: false };
    }
    setCachedTeamUsage(uuid, next);
    return { teamUuid: uuid, usage: next, loading: false };
  });
}

export async function refreshTeamBilling(
  uuid: string,
  opts: { showLoading?: boolean } = {}
): Promise<void> {
  if (inFlightFor === uuid) return;
  inFlightFor = uuid;

  const current = get(store);
  const showSkeleton = opts.showLoading && !current.usage && current.teamUuid === uuid;
  if (showSkeleton) {
    store.update((s) => (s.teamUuid === uuid ? { ...s, loading: true } : s));
  }

  try {
    const res = (await api.getTeamUsage(uuid)) as ApiResponse<TeamUsageSnapshot>;
    if (get(store).teamUuid !== uuid) return;
    if (res?.success && res.data) {
      applyUsage(uuid, res.data);
    }
  } catch (err) {
    console.error('Failed to load team usage', err);
  } finally {
    inFlightFor = null;
    store.update((s) => (s.teamUuid === uuid ? { ...s, loading: false } : s));
  }
}

function onTeamChanged(uuid: string | null) {
  if (!uuid) {
    store.set({ teamUuid: null, usage: null, loading: false });
    return;
  }
  const cached = getCachedTeamUsage(uuid);
  store.set({ teamUuid: uuid, usage: cached, loading: !cached });
  void refreshTeamBilling(uuid, { showLoading: !cached });
}

function handleBillingEvent() {
  const uuid = get(store).teamUuid;
  if (uuid) {
    void refreshTeamBilling(uuid);
  }
}

auth.subscribe((state) => {
  const uuid = state?.user?.active_team_uuid ?? null;
  if (uuid === trackedTeamUuid) return;
  trackedTeamUuid = uuid;
  onTeamChanged(uuid);
});

if (typeof window !== 'undefined') {
  window.addEventListener('billing-updated', handleBillingEvent);
  window.addEventListener('team-updated', handleBillingEvent);
}

export const teamBilling = { subscribe: store.subscribe };
